import { collection, getDocs, limit, query, where } from 'firebase/firestore'
import { db, sendNotificationToTopic, sendOrderStatusNotificationCallable } from '@/firebase'
import { cityAdminTopic, entityTopic, officeTopic, SUPER_ADMIN_TOPIC } from '@/lib/fcmTopics'

/** نصوص إشعار تغيير الحالة (مطابقة للتطبيق) */
export const ORDER_STATUS_PUSH_LABELS = {
  '1': 'جديد',
  '2': 'جاري التجهيز',
  '3': 'في الطريق',
  '4': 'مكتمل',
  '5': 'ملغي'
}

export function orderStatusPushLabel(statusId) {
  return ORDER_STATUS_PUSH_LABELS[String(statusId)] || String(statusId || '—')
}

export async function fetchUserFcmToken(userId) {
  const uid = String(userId || '').trim()
  if (!uid) return ''

  try {
    const snap = await getDocs(
      query(collection(db, 'users'), where('user_id', '==', uid), limit(3))
    )
    if (snap.empty) return ''
    const data = snap.docs[0].data() || {}
    return String(data.fcm_token || data.fcmToken || data.fcmtoken || data.token || '').trim()
  } catch {
    return ''
  }
}

function buildNewOrderPushText({ orderid, username, restaurantName, totalprice, city }) {
  const id = String(orderid || '').trim()
  const user = String(username || 'عميل').trim()
  const rest = String(restaurantName || 'مطعم').trim()
  const cityLabel = String(city || '').trim()
  const total = String(totalprice ?? '').trim()
  const totalPart = total ? ` — ${total} ج.م` : ''
  const cityPart = cityLabel ? ` (${cityLabel})` : ''
  return {
    title: 'طلب جديد',
    body: `طلب #${id} من ${user} — ${rest}${cityPart}${totalPart}`
  }
}

/** إشعار Topics: مكتب المدينة + أدمن المدينة + صاحب المطعم + سوبر أدمن */
export async function notifyNewRestaurantOrderTopics(orderPayload, restaurantMeta = {}) {
  const order = orderPayload?.payload || orderPayload || {}
  const city = String(order.where || restaurantMeta.where || '').trim()
  const entityId = String(order.resturant || restaurantMeta.id || '').trim()
  const { title, body } = buildNewOrderPushText({
    orderid: order.orderid,
    username: order.username,
    restaurantName: restaurantMeta.name_ar || restaurantMeta.name,
    totalprice: order.totalprice,
    city
  })

  const skipOffice =
    restaurantMeta.deliveryByOffice === false ||
    restaurantMeta.orderType === 'hall' ||
    order.orderType === 'hall' ||
    order.orderType === 'pickup'
  const topics = [
    ...(skipOffice ? [] : [officeTopic(city)]),
    cityAdminTopic(city),
    entityTopic(entityId),
    SUPER_ADMIN_TOPIC
  ].filter(Boolean)

  const results = await Promise.allSettled(
    topics.map((topic) => sendNotificationToTopic(topic, title, body))
  )

  const failed = results
    .map((r, i) => (r.status === 'rejected' ? topics[i] : null))
    .filter(Boolean)

  if (failed.length) {
    console.warn('[notifyNewRestaurantOrderTopics] failed topics:', failed)
  }

  return { topics, failed }
}

function buildNewHatlyPushText({ orderid, username, entityName, totalDue, city }) {
  const id = String(orderid || '').trim()
  const user = String(username || 'عميل').trim()
  const place = String(entityName || 'هاتلي').trim()
  const cityLabel = String(city || '').trim()
  const total = String(totalDue ?? '').trim()
  const totalPart = total ? ` — ${total} ج.م` : ''
  const cityPart = cityLabel ? ` (${cityLabel})` : ''
  return {
    title: 'طلب هاتلي جديد',
    body: `طلب #${id} من ${user} — ${place}${cityPart}${totalPart}`
  }
}

/** إشعار Topics: مكتب المدينة + أدمن المدينة + سوبر أدمن + صاحب الكيان (إن وُجد) */
export async function notifyNewHatlyOrderTopics(orderPayload, entityMeta = {}) {
  const order = orderPayload?.payload || orderPayload || {}
  const city = String(order.where || order.city || '').trim()
  const entityId = String(order.entityId || entityMeta.entityId || '').trim()
  const entityName = String(order.entityName || entityMeta.entityName || '').trim()
  const { title, body } = buildNewHatlyPushText({
    orderid: order.orderid,
    username: order.username,
    entityName: entityName || 'هاتلي',
    totalDue: order.totalDue,
    city
  })

  const skipOffice = entityMeta.deliveryByOffice === false
  const topics = [
    ...(skipOffice ? [] : [officeTopic(city)]),
    cityAdminTopic(city),
    SUPER_ADMIN_TOPIC
  ]
  if (entityId) topics.push(entityTopic(entityId))

  const uniqueTopics = [...new Set(topics.filter(Boolean))]

  const results = await Promise.allSettled(
    uniqueTopics.map((topic) => sendNotificationToTopic(topic, title, body))
  )

  const failed = results
    .map((r, i) => (r.status === 'rejected' ? uniqueTopics[i] : null))
    .filter(Boolean)

  if (failed.length) {
    console.warn('[notifyNewHatlyOrderTopics] failed topics:', failed)
  }

  return { topics: uniqueTopics, failed }
}

/** إشعار العميل على جهازه عند تغيير الحالة */
export async function notifyOrderStatusChange(order, newStatusId, updatedBy = '') {
  const statusKey = String(newStatusId || '').trim()
  if (!statusKey || !ORDER_STATUS_PUSH_LABELS[statusKey]) {
    return { ok: false, skipped: true, reason: 'status_not_notifiable' }
  }

  const userId = String(order?.user_id || order?.userid || '').trim()
  const fcmToken = await fetchUserFcmToken(userId)
  if (!fcmToken) {
    return { ok: false, skipped: true, reason: 'no_fcm_token' }
  }

  const orderId = String(order?.orderid || order?.orderId || '').trim()
  const orderSource = order?.from === 'hatly' ? 'hatly' : 'orders'
  const newStatusLabel = orderStatusPushLabel(statusKey)
  const by = String(updatedBy || 'الإدارة').trim()
  const title = 'تحديث حالة الطلب'
  const body = `تم تحديث حالة طلبك #${orderId} إلى ${newStatusLabel}`

  try {
    await sendOrderStatusNotificationCallable({
      fcmToken,
      orderId,
      orderSource,
      newStatus: statusKey,
      newStatusLabel,
      updatedBy: by,
      title,
      body
    })
    return { ok: true }
  } catch (e) {
    console.warn('[notifyOrderStatusChange]', e)
    return { ok: false, error: e?.message || String(e) }
  }
}
